// @ts-strict-ignore
import React from 'react';
import {
  Box,
  Icon,
  IconName,
  Text,
} from '@usealloy/component-library';
import type { FeatureFlags, Permissions } from '~/helpers/typescript';
import { getIncomingDataRoutingCategories, getOutgoingDataRoutingCategories } from 'flow/service/dataRoutes';
import type { FormState } from './useNodeEditorForm';
import { getActionTabKey } from './useNodeEditorForm';
import type {
  Action,
  NodeEditorTab,
  SchemaInstance,
  WorkflowProps,
} from 'flow/store/engine/types';
import type { NodeEditorDrawerTabConfig } from './types';
import { ActionType } from 'flow/store/engine/constants';
import {
  isActionOfType,
  getActionTypeText,
  actionHasDisplayName,
  getActionName,
} from 'flow/service/actions';
import { StyledCounter, StyledDependentsLabel } from './DependentsTab/styles';
import AttributeConfigurationFooter from './AttributeConfiguration/Footer';
import ServiceDataRoutingTab from './ServiceTabs/ServiceDataRoutingTab';
import DependentsTab from './DependentsTab';
import InputAttributeConfigurationTab from './AttributeConfiguration/InputAttributeConfigurationTab';
import LinkedChildActionFooter from './ThresholdsTab/LinkedChildActionFooter';
import MatrixOptionsTab from './MatrixOptionsTab';
import OutputAttributeExpressionTab, { Footer as OutputAttributeExpressionFooter } from './OutputAttributeExpressionTab';
import OutputAttributeOptionsTab from './AttributeConfiguration/OutputAttributeOptionsTab';
import ServiceOptionsTab, { Footer as ServiceOptionsFooter } from './ServiceTabs/ServiceOptionsTab';
import ServiceLocalSettingsTab, { Footer as ServiceLocalSettingsFooter } from './ServiceTabs/ServiceLocalSettingsTab';
import TagGlobalSettingsTab, { Footer as TagGlobalSettingsFooter } from './TagGlobalSettingsTab';
import TagLocalSettingsTab, { Footer as TagLocalSettingsFooter } from './TagLocalSettingsTab';
import ThresholdsTab from './ThresholdsTab';
import NodeTooltip from '../Graph/nodes/BaseNode/NodeTooltip';
import type { UserContextValue } from '~/dashboard/context';

export function getDrawerTitleText(focusedAction: Action): React.ReactNode {
  if (!focusedAction || focusedAction.type === ActionType.PREVIEW) return null;

  const label = isActionOfType(focusedAction, ActionType.SERVICE)
    ? focusedAction.service.name
    : getActionTypeText(focusedAction);
  const isServiceWithoutDisplayName = isActionOfType(focusedAction, ActionType.SERVICE)
    && !actionHasDisplayName(focusedAction);
  return (
    <>
      <Text as="h3" size={3} style={{ color: 'var(--cl-gray-600)', marginRight: '5px' }}>
        {isServiceWithoutDisplayName ? label : `${label}:`}
      </Text>
      {!isServiceWithoutDisplayName && getActionName(focusedAction)}
    </>
  );
}


function generateIcon(iconName: IconName, type: ActionType) {
  return (
    <Box gap={2} data-dd-action-name={`drawer-header-icon-${type}`}>
      <Icon color="var(--cl-gray-600)" icon={iconName} />
    </Box>
  );
}

export function getDrawerTitleIcon(focusedAction: Action) {
  if (!focusedAction) {
    return null;
  }
  switch (focusedAction.type) {
    case ActionType.INPUT:
      return generateIcon(IconName.Input, focusedAction.type);
    case ActionType.MATRIX_MODEL:
    case ActionType.IMPORTED_MATRIX_MODEL:
      return generateIcon(IconName.Matrix, focusedAction.type);
    case ActionType.OUTPUT:
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      return generateIcon(IconName.Output, focusedAction.type);
    case ActionType.SERVICE:
      return generateIcon(IconName.Service, focusedAction.type);
    case ActionType.TAG:
      return generateIcon(IconName.Tag, focusedAction.type);
    case ActionType.OUTCOME_REASON:
      return generateIcon(IconName.Stamp, focusedAction.type);
    default:
      return null;
  }
}


function createOptionLabel(label: string) {
  const dataName = label.replace(' ', '-').toLowerCase();
  return (
    <div data-dd-action-name={`drawer-tab-${dataName}`}>
      {label}
    </div>
  );
}

export function getDrawerTabs(
  args: {
    dataRoutes: SchemaInstance['dataRoutes'];
    featureFlags: Partial<FeatureFlags>;
    permissions: Partial<Permissions>;
    focusedAction: Action;
    options: {
      isCreatingLinkedChildAction: boolean;
      numDependents: number;
    };
    services: WorkflowProps['services'];
    inEditMode: boolean;
    breadcrumbs: UserContextValue['breadcrumbs'];
    handleFormAutoSave(formState: FormState): void;
  },
): NodeEditorDrawerTabConfig[] {
  const {
    dataRoutes,
    featureFlags,
    permissions,
    focusedAction,
    handleFormAutoSave,
    options: { isCreatingLinkedChildAction, numDependents },
    services,
    inEditMode,
  } = args;

  if (!focusedAction) {
    return [];
  }
  const DependentsLabel = (
    <StyledDependentsLabel data-dd-action-name="drawer-tab-dependents">
      Dependents
      <StyledCounter>{numDependents}</StyledCounter>
    </StyledDependentsLabel>
  );

  let dataRoutingTab: NodeEditorDrawerTabConfig[] = [];

  if (focusedAction.type === ActionType.SERVICE && inEditMode) {
    const availableIncomingCategories = getIncomingDataRoutingCategories({
      action: focusedAction,
      dataRoutes,
      services,
    });

    const availableOutgoingCategories = getOutgoingDataRoutingCategories({
      action: focusedAction,
      dataRoutes,
      services,
    });

    if (availableIncomingCategories.length || availableOutgoingCategories.length) {
      dataRoutingTab = [{
        label: createOptionLabel('Data Routing'),
        drawerContent: <ServiceDataRoutingTab />,
        value: 'datarouting',
      }];
    }
  }

  let serviceLocalSettingsTab: NodeEditorDrawerTabConfig[] = [];
  if (permissions?.['READ:CustomerServices:null'] && featureFlags.enableServiceConfig && isActionOfType(focusedAction, ActionType.SERVICE)) {
    const serviceLocalSettingsLabel = createOptionLabel('Local Settings');
    serviceLocalSettingsTab = [{
      label: serviceLocalSettingsLabel,
      drawerContent: <ServiceLocalSettingsTab handleAutoSave={handleFormAutoSave} />,
      value: 'configuration',
      id: 'workflow-editor-service-local-settings-drawer',
    }];
  }

  const configurationLabel = createOptionLabel('Configuration');
  const optionsLabel = createOptionLabel('Options');
  const thresholdLabel = createOptionLabel('Thresholds');
  switch (focusedAction.type) {
    case ActionType.INPUT: {
      return [
        {
          drawerContent: <InputAttributeConfigurationTab handleAutoSave={handleFormAutoSave} />,
          label: configurationLabel,
          value: 'configuration' as NodeEditorTab,
        },
        {
          drawerContent: <DependentsTab />,
          label: DependentsLabel,
          value: 'dependents' as NodeEditorTab,
        },
      ];
    }
    case ActionType.OUTPUT: {
      return [
        {
          label: createOptionLabel('Expression'),
          drawerContent: <OutputAttributeExpressionTab
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
        {
          drawerContent: <DependentsTab />,
          label: DependentsLabel,
          value: 'dependents' as NodeEditorTab,
        },
        ...(inEditMode ? [
          {
            label: optionsLabel,
            drawerContent: <OutputAttributeOptionsTab handleAutoSave={handleFormAutoSave} />,
            value: 'options' as NodeEditorTab,
          },
        ] : []),
      ];
    }
    case ActionType.TAG: {
      return [
        {
          label: thresholdLabel,
          drawerContent: <ThresholdsTab
            action={focusedAction}
            ruleNumberConfig={{ isDragEnabled: true }}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: <DependentsTab />,
          value: 'dependents',
        },
        ...(inEditMode ? [
          {
            label: createOptionLabel('Local Settings'),
            drawerContent: <TagLocalSettingsTab handleAutoSave={handleFormAutoSave} />,
            // using 'options' instead of creating new NodeEditorTab value
            value: 'options' as NodeEditorTab,
          },
          {
            label: createOptionLabel('Global Settings'),
            drawerContent: <TagGlobalSettingsTab />,
            // using 'configuration' instead of creating new NodeEditorTab value
            value: 'configuration' as NodeEditorTab,
          },
        ] : []),
      ];
    }
    case ActionType.SERVICE:
      return [
        {
          label: thresholdLabel,
          drawerContent: <ThresholdsTab
            ruleNumberConfig={{ isDragEnabled: true }}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: <DependentsTab />,
          value: 'dependents',
        },
        ...(inEditMode ? [
          ...dataRoutingTab,
          {
            label: optionsLabel,
            drawerContent: <ServiceOptionsTab handleAutoSave={handleFormAutoSave} />,
            value: 'options' as NodeEditorTab,
          },
          ...serviceLocalSettingsTab,
        ] : []),
      ];
    case ActionType.PREVIEW:
      return [
        {
          label: thresholdLabel,
          drawerContent: <ThresholdsTab
            ruleNumberConfig={{ isDragEnabled: true }}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
      ];
    case ActionType.MATRIX_MODEL:
      return [
        {
          label: configurationLabel,
          drawerContent: <ThresholdsTab
            ruleNumberConfig={{ isDragEnabled: true }}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
        {
          label: DependentsLabel,
          drawerContent: <DependentsTab />,
          value: 'dependents',
        },
        ...(inEditMode ? [
          {
            label: optionsLabel,
            drawerContent: <MatrixOptionsTab handleAutoSave={handleFormAutoSave} />,
            value: 'options' as NodeEditorTab,
          },
        ] : []),
      ];
    default:
      return [
        {
          label: thresholdLabel,
          drawerContent: <ThresholdsTab
            ruleNumberConfig={{ isDragEnabled: true }}
            // To avoid circular import, passing NodeTooltip into ThresholdsTab
            ActionHoverTooltip={props => (
              <NodeTooltip canClickToAction {...props} />
            )}
          />,
          value: 'rule',
        },
        {
          disabled: isCreatingLinkedChildAction,
          label: DependentsLabel,
          drawerContent: <DependentsTab />,
          value: 'dependents',
        },
      ];
  }
}

export function getDrawerTabFooter(
  activeNodeEditorTab: NodeEditorTab,
  focusedAction: Action,
) {
  if (!focusedAction) {
    return null;
  }

  const tabFooterMap = {
    [getActionTabKey(ActionType.PREVIEW, 'rule')]: LinkedChildActionFooter,
    [getActionTabKey(ActionType.INPUT, 'configuration')]: AttributeConfigurationFooter,
    [getActionTabKey(ActionType.OUTPUT, 'options')]: AttributeConfigurationFooter,
    [getActionTabKey(ActionType.SERVICE, 'options')]: ServiceOptionsFooter,
    [getActionTabKey(ActionType.SERVICE, 'configuration')]: ServiceLocalSettingsFooter, // TODO: update it to 'options' once we clean out the legacy service options
    [getActionTabKey(ActionType.TAG, 'configuration')]: TagGlobalSettingsFooter,
    [getActionTabKey(ActionType.TAG, 'options')]: TagLocalSettingsFooter,
    [getActionTabKey(ActionType.OUTPUT, 'rule')]: OutputAttributeExpressionFooter,
    // add other tab footers here
  };

  const tabKey = getActionTabKey(focusedAction.type, activeNodeEditorTab);
  return tabFooterMap[tabKey];
}
